import { isAccessRefusal, isRecordLoad } from './recordAccess';

/**
 * 404s the global API error toast stays quiet about, because the caller expects them and handles
 * them itself:
 * - GET of a medical note: the page generates the note when there is none yet.
 * - GET of a procedure CPT audit: none exists until a V3 extraction has run; the coding section
 *   falls back to its legacy view (AMP-160). Toasting it showed "No V3 extraction audit found
 *   for this appointment" on every validation page opened before an extraction.
 */
export function isQuietNotFound(status: number | undefined, url: string, method?: string): boolean {
  if (status !== 404) return false;
  const path = url.split('?')[0];
  const isGet = !method || method.toLowerCase() === 'get';
  const medicalNoteRead = path.includes('/medical-note') && !path.includes('/generate') && !path.includes('/sign') && !path.includes('/send-to-ehr');
  const procedureCptAudit = isGet && /\/procedure-cpt\/audit$/.test(path);
  return medicalNoteRead || procedureCptAudit;
}

/**
 * A refused load of an encounter or appointment the user can't open. The page shows its
 * Access Denied screen, so the global toast stays quiet. Writes and other 403s keep their toast.
 */
export function isQuietAccessRefusal(status: number | undefined, url: string, method?: string, code?: string): boolean {
  const isGet = !method || method.toLowerCase() === 'get';
  return isGet && isRecordLoad(url) && isAccessRefusal(status, url, code);
}

/**
 * A 502 from Send to EHR or its retry is the delivery's own failure, not a server error (AMP-109).
 * The page tells the clinician the note was not delivered and what to do, so the global
 * "Server error. Please try again later." toast stays quiet about it.
 */
export function isQuietDeliveryFailure(status: number | undefined, url: string): boolean {
  if (status !== 502) return false;
  return /\/(send-to-ehr|ehr-delivery-retry)$/.test(url.split('?')[0]);
}
